import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Select, Spin } from "antd";
import { useTranslation } from "react-i18next";
import ApiConfig from "../api/ApiConfig";
import {
  simplePostCall,
  simpleGetCall,
  simplePUTCall,
} from "../api/ApiServices";
import { notifyError, notifySuccess } from "./notify";

const { Option } = Select;

const emptyForm = {
  make: "",
  model: "",
  year: "",
  mileage: "",
  fuelType: "Petrol",
  transmission: "Automatic",
  isGccSpec: true,
  accident: "No Accident",
  service: "Full Service History",
  variant: "",
  owners: "First Owner",
  presentPrice: "",
  location: "Dubai",
};

// Pull an estimated price range out of a (flexible) prediction response.
const pickRange = (data) => {
  if (!data) return {};
  const min =
    data.min_price ??
    data.minPrice ??
    data.estimated_min ??
    data.price_min ??
    data.low ??
    data.min ??
    data.predicted_min;
  const max =
    data.max_price ??
    data.maxPrice ??
    data.estimated_max ??
    data.price_max ??
    data.high ??
    data.max ??
    data.predicted_max;
  const single =
    data.predicted_price ?? data.price ?? data.estimated_value ?? data.value;
  const cls =
    data.vehicle_class || data.class || data.category || data.segment || "";
  const demand = data.demand || data.resale_demand || data.note || "";
  return {
    min: min ?? single,
    max: max ?? single,
    cls,
    demand,
  };
};

const fmt = (n) => (n || n === 0 ? Number(n).toLocaleString("en-US") : "--");

// Normalise the (flexible) "valuation single" response into a record object.
const pickRecord = (res) => {
  if (!res) return null;
  const d = res.data || res.valuation || res.result || res;
  if (d && typeof d === "object" && (d.valuation_id || d.make || d.year || d.mileage)) {
    return d;
  }
  return null;
};

const VehicleValuationModal = ({ show, onHide, vehicle }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false); // saving / predicting
  const [fetching, setFetching] = useState(false); // loading existing record
  const [result, setResult] = useState(null);
  // The valuation record is keyed by the vehicle. When the GET returns a
  // record we are in "update" mode, otherwise we are in "create" mode.
  const [hasExisting, setHasExisting] = useState(false);

  const vehicleId = vehicle?.vehicle_id;

  // On open: load this vehicle's existing valuation. If one exists, autofill
  // the form (update mode). If not, start a fresh form (create mode).
  useEffect(() => {
    if (!show) return;
    setResult(null);
    setHasExisting(false);
    // Prefill whatever we already know about the vehicle.
    const base = {
      ...emptyForm,
      model: vehicle?.vehicle_model_no || "",
      fuelType: vehicle?.vehicle_type_fuel || emptyForm.fuelType,
    };
    setForm(base);

    if (!vehicleId) return;
    setFetching(true);
    simpleGetCall(ApiConfig.GET_VEHICLE_VALUATION_SINGLE + vehicleId)
      .then((res) => {
        const d = pickRecord(res);
        if (d) {
          setHasExisting(true);
          // The API returns snake_case keys, but accepts camelCase on write.
          // Read both so the form autofills regardless of casing.
          setForm((f) => ({
            ...f,
            make: d.make ?? f.make,
            model: d.model ?? f.model,
            year: d.year ?? f.year,
            mileage: d.mileage ?? "",
            fuelType: d.fuel_type ?? d.fuelType ?? f.fuelType,
            transmission: d.transmission ?? f.transmission,
            isGccSpec: d.is_gcc_spec ?? d.isGccSpec ?? f.isGccSpec,
            accident: d.accident ?? f.accident,
            service: d.service ?? f.service,
            variant: d.variant ?? "",
            owners: d.owners ?? f.owners,
            presentPrice: d.present_price ?? d.presentPrice ?? "",
            location: d.location ?? f.location,
          }));
        }
      })
      .catch((err) => console.log("valuation single err", err))
      .finally(() => setFetching(false));
  }, [show, vehicleId, vehicle]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Save the valuation record: UPDATE if one already exists, else ADD a new one.
  const saveValuation = () => {
    const fields = {
      make: form.make,
      model: form.model,
      year: Number(form.year),
      mileage: Number(form.mileage),
      fuelType: form.fuelType,
      transmission: form.transmission,
      isGccSpec: form.isGccSpec,
      accident: form.accident,
      service: form.service,
      variant: form.variant,
      owners: form.owners,
      presentPrice: Number(form.presentPrice) || 0,
      location: form.location,
    };
    if (hasExisting) {
      // The valuation is keyed by the vehicle, so valuation_id == vehicle_id.
      return simplePUTCall(
        ApiConfig.UPDATE_VEHICLE_VALUATION,
        JSON.stringify({ valuation_id: vehicleId, vehicle_id: vehicleId, ...fields })
      );
    }
    return simplePostCall(
      ApiConfig.ADD_VEHICLE_VALUATION,
      JSON.stringify({ vehicle_id: vehicleId, ...fields })
    );
  };

  // Call the price-prediction model (plain fetch, no auth headers).
  const predictPrice = async () => {
    const res = await fetch(ApiConfig.PREDICT_VEHICLE_PRICE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        make: form.make,
        model: form.model,
        year: Number(form.year),
        mileage: Number(form.mileage),
        accident: form.accident,
        service: form.service,
        variant: form.variant,
        transmission: form.transmission,
        owners: form.owners,
        presentPrice: Number(form.presentPrice) || 0,
      }),
    });
    return res.json();
  };

  const handleGetValuation = async () => {
    if (!form.make || !form.model) {
      notifyError(t("Please enter Make and Model"));
      return;
    }
    if (!form.year || !form.mileage) {
      notifyError(t("Please enter Year and Mileage"));
      return;
    }
    setLoading(true);
    try {
      // 1) Persist the record (create new or update existing).
      try {
        await saveValuation();
        notifySuccess(
          hasExisting ? t("Valuation updated") : t("Valuation saved")
        );
        setHasExisting(true);
      } catch (e) {
        console.log("save valuation err", e);
        notifyError(t("Could not save the valuation."));
      }
      // 2) Get the estimated price and show the result page.
      const pred = await predictPrice();
      setResult({ ...pickRange(pred), input: { ...form } });
    } catch (err) {
      console.log("predict err", err);
      notifyError(t("Could not get valuation. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setResult(null);
  };

  const primaryLabel = loading
    ? t("Saving...")
    : hasExisting
    ? t("Update & Get Valuation")
    : t("Save & Get Valuation");

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          {result ? t("Estimated Market Value") : t("Car Valuation")}
          {!result && !fetching ? (
            <span
              className="px-2 py-1"
              style={{
                fontSize: "11px",
                fontWeight: 600,
                borderRadius: "12px",
                marginLeft: "8px",
                background: hasExisting ? "#e6f4ff" : "#f0f5ee",
                color: hasExisting ? "#0958d9" : "#3a7d20",
              }}
            >
              {hasExisting ? t("Existing valuation") : t("New valuation")}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: "120px" }}>
        {fetching ? (
          /* ===================== LOADING ===================== */
          <div className="text-center py-5">
            <Spin />
            <p className="mt-3 mb-0" style={{ color: "#888" }}>
              {t("Loading valuation details...")}
            </p>
          </div>
        ) : !result ? (
          /* ===================== FORM ===================== */
          <div>
            <h6
              className="mb-3"
              style={{ color: "#8f4300", fontWeight: 600, fontSize: "13px" }}
            >
              {t("Vehicle Details")}
            </h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label>{t("Make")}</label>
                <input
                  className="form-control"
                  value={form.make}
                  onChange={(e) => setField("make", e.target.value)}
                  placeholder={t("e.g. Audi")}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("Model")}</label>
                <input
                  className="form-control"
                  value={form.model}
                  onChange={(e) => setField("model", e.target.value)}
                  placeholder={t("e.g. A6")}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("Variant")}</label>
                <input
                  className="form-control"
                  value={form.variant}
                  onChange={(e) => setField("variant", e.target.value)}
                  placeholder={t("e.g. 40 TFSI")}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("Year")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.year}
                  onChange={(e) => setField("year", e.target.value)}
                  placeholder={t("e.g. 2020")}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("Mileage (KM)")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.mileage}
                  onChange={(e) => setField("mileage", e.target.value)}
                  placeholder={t("e.g. 40000")}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("Present Price")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.presentPrice}
                  onChange={(e) => setField("presentPrice", e.target.value)}
                  placeholder={t("e.g. 40000")}
                />
              </div>
            </div>

            <h6
              className="mb-3 mt-2"
              style={{ color: "#8f4300", fontWeight: 600, fontSize: "13px" }}
            >
              {t("Condition & History")}
            </h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="d-block">{t("Fuel Type")}</label>
                <Select
                  style={{ width: "100%", height: "40px" }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={form.fuelType}
                  onChange={(v) => setField("fuelType", v)}
                >
                  <Option value="Petrol">{t("Petrol")}</Option>
                  <Option value="Diesel">{t("Diesel")}</Option>
                  <Option value="Electric">{t("Electric")}</Option>
                  <Option value="Hybrid">{t("Hybrid")}</Option>
                </Select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block">{t("Transmission")}</label>
                <Select
                  style={{ width: "100%", height: "40px" }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={form.transmission}
                  onChange={(v) => setField("transmission", v)}
                >
                  <Option value="Automatic">{t("Automatic")}</Option>
                  <Option value="Manual">{t("Manual")}</Option>
                </Select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block">{t("Owners")}</label>
                <Select
                  style={{ width: "100%", height: "40px" }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={form.owners}
                  onChange={(v) => setField("owners", v)}
                >
                  <Option value="First Owner">{t("First Owner")}</Option>
                  <Option value="Second Owner">{t("Second Owner")}</Option>
                  <Option value="Third Owner">{t("Third Owner")}</Option>
                  <Option value="Fourth Owner & Above">
                    {t("Fourth Owner & Above")}
                  </Option>
                </Select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block">{t("Specification")}</label>
                <Select
                  style={{ width: "100%", height: "40px" }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={form.isGccSpec}
                  onChange={(v) => setField("isGccSpec", v)}
                >
                  <Option value={true}>{t("GCC Specification")}</Option>
                  <Option value={false}>{t("Non-GCC")}</Option>
                </Select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block">{t("Accident History")}</label>
                <Select
                  style={{ width: "100%", height: "40px" }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={form.accident}
                  onChange={(v) => setField("accident", v)}
                >
                  <Option value="No Accident">{t("No Accident")}</Option>
                  <Option value="Minor Accident">{t("Minor Accident")}</Option>
                  <Option value="Major Accident">{t("Major Accident")}</Option>
                </Select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block">{t("Service History")}</label>
                <Select
                  style={{ width: "100%", height: "40px" }}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  value={form.service}
                  onChange={(v) => setField("service", v)}
                >
                  <Option value="Full Service History">
                    {t("Full Service History")}
                  </Option>
                  <Option value="Partial Service History">
                    {t("Partial Service History")}
                  </Option>
                  <Option value="No Service History">
                    {t("No Service History")}
                  </Option>
                </Select>
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("Location")}</label>
                <input
                  className="form-control"
                  value={form.location}
                  onChange={(e) => setField("location", e.target.value)}
                />
              </div>
            </div>
          </div>
        ) : (
          /* ===================== RESULT ===================== */
          <div>
            <div
              className="text-center p-4 mb-3"
              style={{
                background: "linear-gradient(135deg, #0e1726 0%, #1f3a5f 100%)",
                borderRadius: "12px",
                color: "#fff",
              }}
            >
              <p style={{ letterSpacing: "2px", opacity: 0.8, margin: 0 }}>
                {t("ESTIMATED MARKET VALUE")}
              </p>
              <h2 style={{ fontWeight: 700, margin: "8px 0" }}>
                {fmt(result.min)} - {fmt(result.max)}{" "}
                <span style={{ fontSize: "16px", opacity: 0.7 }}>AED</span>
              </h2>
              {result.cls ? (
                <span
                  className="px-3 py-1"
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    borderRadius: "16px",
                    fontSize: "13px",
                  }}
                >
                  {result.cls}
                </span>
              ) : null}
            </div>

            <h6 className="mb-3">{t("Vehicle Information Summary")}</h6>
            <div className="row">
              {[
                ["Vehicle", `${result.input.make} ${result.input.model}`.trim()],
                ["Year", result.input.year],
                ["Mileage", `${fmt(result.input.mileage)} KM`],
                [
                  "Spec",
                  result.input.isGccSpec ? "GCC Specification" : "Non-GCC",
                ],
                ["Fuel", result.input.fuelType],
                ["Owners", result.input.owners],
                ["Location", result.input.location],
                ["Service History", result.input.service],
                ["Accident History", result.input.accident],
              ].map(([label, value]) => (
                <div className="col-md-6 mb-3" key={label}>
                  <p
                    className="mb-1"
                    style={{
                      fontSize: "12px",
                      color: "#8f4300",
                      fontWeight: 600,
                    }}
                  >
                    {t(label)}
                  </p>
                  <p className="mb-0">{value || "--"}</p>
                </div>
              ))}
            </div>
            {result.demand ? (
              <div
                className="p-2 mt-2 text-center"
                style={{ background: "#eefaf0", borderRadius: "8px" }}
              >
                ⚡ {result.demand}
              </div>
            ) : null}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!result ? (
          <>
            <button className="cx-btn-1" onClick={onHide}>
              {t("Cancel")}
            </button>
            <button
              className="cx-btn-2"
              onClick={handleGetValuation}
              disabled={loading || fetching}
            >
              {primaryLabel}
            </button>
          </>
        ) : (
          <>
            <button className="cx-btn-1" onClick={startNew}>
              {t("Edit Details")}
            </button>
            <button className="cx-btn-2" onClick={onHide}>
              {t("Close")}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default VehicleValuationModal;
